import React, { useState, useEffect } from 'react'
import { useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { abi } from "./nftabi";
import { toast } from './App';

export default function EligibilityScreen() {
    const [minting, setMinting] = useState(false);
    const [mintingStage, setMintingStage] = useState('idle'); // idle, pending, confirming, success, error
    
    const { writeContract, data: hash, error, isPending } = useWriteContract();
    
    const { 
        isLoading: isConfirming, 
        isSuccess: isConfirmed,
        data: receipt
    } = useWaitForTransactionReceipt({
        hash,
    });

    // Handle minting stages
    useEffect(() => {
        if (isPending) {
            setMintingStage('pending');
            setMinting(true);
        } else if (isConfirming) {
            setMintingStage('confirming');
        } else if (isConfirmed) {
            setMintingStage('success');
            setMinting(false);
            toast.success('NFT minted successfully!');
        } else if (error) {
            setMintingStage('error');
            setMinting(false);
            toast.error('Minting error: ' + error.message);
        }
    }, [isPending, isConfirming, isConfirmed, error]);

    const handleMint = async () => {
        try {
            setMinting(true);
            setMintingStage('pending');
            
            // Call the smart contract mint function
            writeContract({
                abi,
                address: "0xe933fB4624eE012592A96900166cF0226Ed25072",
                functionName: "mint",
                chainId: 11155111,
                // Add any required arguments for your mint function
                // args: [/* mint arguments if needed */]
            });
        } catch (err) {
            console.error('Minting error:', err);
            setMinting(false);
            setMintingStage('error');
        }
    };

    const getMintingText = () => {
        switch (mintingStage) {
            case 'pending':
                return 'Confirm in Wallet...';
            case 'confirming':
                return 'Minting in Progress...';
            case 'success':
                return 'Mint Another';
            case 'error':
                return 'Try Again';
            default:
                return 'Mint Croissant NFT';
        }
    };

    const getStatusMessage = () => {
        switch (mintingStage) {
            case 'pending':
                return 'Please confirm the transaction in your wallet';
            case 'confirming':
                return 'Your NFT is being minted on the blockchain...';
            case 'success':
                return '🎉 Croissant NFT minted successfully!';
            case 'error':
                return `❌ Error: ${error?.message || 'Something went wrong'}`;
            default:
                return null;
        }
    };

    return (
        <div className="bg-gradient-to-br from-yellow-100 to-pink-100 border-4 border-black p-10 rounded-3xl shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] text-center max-w-3xl mx-auto mt-2">
            <div className="mb-8">
                <h1 className="text-5xl font-extrabold text-yellow-900 mb-2 drop-shadow-lg">You're a Social Star!</h1>
                <p className="text-xl text-gray-700 mb-4 font-medium">Because you have more than <span className="font-bold text-blue-700">1000 followers</span> on Twitter, you are eligible to mint a special NFT.</p>
            </div>

            <div className="bg-orange-100 border-4 border-black w-64 h-64 mx-auto mb-8 flex items-center justify-center rounded-2xl shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
                <img 
                    src="https://apricot-immense-carp-804.mypinata.cloud/ipfs/bafybeihp2edewjnl7hlxaeak32bj72aazogrp4nq3dxkz6ywfl235pl3li" 
                    alt="Croissant NFT" 
                    className="w-56 h-56 object-cover rounded-xl border-2 border-yellow-400 shadow-md" 
                />
            </div>

            <div className="bg-white/80 border-2 border-dashed border-yellow-400 p-4 mb-8 rounded-xl shadow-sm">
                <p className="text-lg text-gray-800 font-semibold flex items-center justify-center gap-2">
                    <span role="img" aria-label="party">🎉</span>
                    You have more than 1000 followers on Twitter, so you are eligible!
                </p>
            </div>

            {/* Status Message */}
            {getStatusMessage() && (
                <div className={`mb-6 p-4 rounded-xl border-2 ${
                    mintingStage === 'success' 
                        ? 'bg-green-100 border-green-400 text-green-800' 
                        : mintingStage === 'error'
                        ? 'bg-red-100 border-red-400 text-red-800'
                        : 'bg-blue-100 border-blue-400 text-blue-800'
                }`}>
                    <p className="font-semibold">{getStatusMessage()}</p>
                </div>
            )}

            {/* Transaction Hash */}
            {hash && (
                <div className="mb-6 p-4 bg-gray-100 border-2 border-gray-300 rounded-xl">
                    <p className="text-sm text-gray-600 mb-2">Transaction Hash:</p>
                    <p className="text-xs font-mono break-all text-gray-800" onClick={() => {navigator.clipboard.writeText(hash); toast.success('Transaction hash copied!')}} style={{cursor:'pointer'}}>{hash}</p>
                    <a 
                        href={`https://sepolia.etherscan.io/tx/${hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline text-sm mt-2 inline-block"
                    >
                        View on Etherscan
                    </a>
                </div>
            )}

            <button
                className={`bg-gradient-to-r from-pink-200 to-yellow-200 border-4 border-black px-12 py-6 font-extrabold text-black text-2xl rounded-xl transition-all shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] hover:scale-105 ${
                    minting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-300'
                }`}
                onClick={handleMint}
                disabled={minting}
            >
                {getMintingText()}
            </button>

            {/* Loading indicator */}
            {(isPending || isConfirming) && (
                <div className="mt-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
            )}
        </div>
    );
}